import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Preferences } from '@capacitor/preferences';

// Cache keys
const AUTH_USER_CACHE_KEY = 'auth_user_cache';

/**
 * Utility class for handling authentication consistently across the app
 */
export class AuthUtils {
  /**
   * Get the current authenticated user from Firebase and cache
   * This combines both Firebase's getCurrentUser and cached data for faster loading
   */
  static async getCurrentUser(): Promise<any> {
    try {
      // Try to get from cache first for immediate response
      const cachedUser = await this.getCachedUser();
      
      // In parallel, fetch the latest from Firebase
      this.refreshUserFromFirebase();
      
      return cachedUser;
    } catch (error) {
      console.error('Error in getCurrentUser:', error);
      return null;
    }
  }

  /**
   * Get the cached user from Preferences
   */
  static async getCachedUser(): Promise<any> {
    try {
      const { value } = await Preferences.get({ key: AUTH_USER_CACHE_KEY });
      return value ? JSON.parse(value) : null;
    } catch (error) {
      console.error('Error getting cached user:', error);
      return null;
    }
  }

  /**
   * Update the cached user
   */
  static async setCachedUser(user: any): Promise<void> {
    try {
      if (user) {
        await Preferences.set({
          key: AUTH_USER_CACHE_KEY,
          value: JSON.stringify(user),
        });
      } else {
        await Preferences.remove({ key: AUTH_USER_CACHE_KEY });
      }
    } catch (error) {
      console.error('Error setting cached user:', error);
    }
  }

  /**
   * Refresh the user from Firebase and update cache
   */
  static async refreshUserFromFirebase(): Promise<any> {
    try {
      const result = await FirebaseAuthentication.getCurrentUser();
      const user = result.user;
      
      // Update cache
      await this.setCachedUser(user);
      
      return user;
    } catch (error) {
      console.error('Error refreshing user from Firebase:', error);
      return null;
    }
  }

  /**
   * Set up auth state change listener
   * @param callback Function to call when auth state changes
   */
  static async setupAuthListener(callback: (user: any) => void): Promise<any> {
    return await FirebaseAuthentication.addListener('authStateChange', async (result) => {
      // Update cache when auth state changes
      await this.setCachedUser(result.user);
      
      // Call the callback with the user
      callback(result.user);
    });
  }

  /**
   * Check if user is signed in
   * @returns boolean indicating if user is signed in
   */
  static async isUserSignedIn(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return !!user;
  }

  /**
   * Remove all auth listeners
   */
  static async removeAllListeners(): Promise<void> {
    await FirebaseAuthentication.removeAllListeners();
  }
}
